import { Button, Dialog, DialogContent, DialogTitle, Grid, GridSize, Stack, TextField, Typography } from "@mui/material";
import Section from "../../../components/Section";
import SimpleTable from "../../../components/SimpleTable";
import { Outfit, PlayerCharacter, PlayerCharacterDisplay, TradeGood, Equipment, Weapon, WeaponBlock, WeaponBlockBase, OutfitBlock, InteractableSave } from "../../../model/character";
import { ReactNode, useEffect, useState } from "react";
import TradeGoodEditor, { GetNewTradeGood } from "./TradeGoodEditor";
import { GetNewOutfit, OutfitEditor } from "./OutfitsEditor";
import { GetNewWeapon, WeaponEditor } from "./WeaponsEditor";
import { GetNewUtility, UtilityEditor } from "./UtilityEditor";

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import SettingsIcon from '@mui/icons-material/Settings';
import EquipmentBagIcon from "../../../components/icons/EquipmentBagIcon";
import MiscIcon from "../../../components/icons/MiscIcon";
import OutfitIcon from "../../../components/icons/OutfitIcon";
import TradeGoodIcon from "../../../components/icons/TradeGoodIcon";
import UtilityIcon from "../../../components/icons/UtilityIcon";
import WeaponIcon from "../../../components/icons/WeaponIcon";
import HeadingBar, { HeadingBarItem } from "../../../components/HeadingBar";
import StashedIcon from "../../../components/icons/Stashed";
import WeaponDetailDisplay from "../../../components/displays/WeaponDetailDisplay";
import OutfitDetailDisplay from "../../../components/displays/OutfitDetailDisplay";
import CollapseClickAway from "../../../components/CollapseClickAway";
import { Modal } from "../../../components/Modal";
import { HexList } from "../../../components/HexList";
import { listData, loadLibraryData, makeListData } from "../../../master/libraryData";
import ButtonFlat from "../../../components/ButtonFlat";


interface EquipmentRow {
    id: string,
    icon: ReactNode,
    name: ReactNode,
    amount?: number,
    slots?: number,
    influence?: number,
    resources?: number,
    knowledge?: number,
    actions: ReactNode,
    details?: ReactNode, //For extra details in the drop down
}

interface EquipmentListProps {
    character: PlayerCharacter;
    characterDisplay: PlayerCharacterDisplay;
    setCharacter: (value: PlayerCharacter) => void
}

const EquipmentList = (props: EquipmentListProps) => {
    const { character, characterDisplay, setCharacter } = props;

    const [ editTradeGood, setEditTradeGood ] = useState<TradeGood>();
    const [ editWeapon, setEditWeapon ] = useState<Weapon>();
    const [ manageWeapon, setManageWeapon ] = useState<Weapon>();
    const [ editOutfit, setEditOutfit ] = useState<Outfit>();
    const [ manageOutfit, setManageOutfit ] = useState<Outfit>();
    const [ editUtility, setEditUtility ] = useState<Equipment>();
    const [ editEquipment, setEditEquipment ] = useState<Equipment>();
    const [ expandId, setExpandId] = useState<string>("");

    const [ showAddModal, setShowAddModal ] = useState(false);

    const handleAddTradeGood = (id: string) => {
        let newEquipment = GetNewTradeGood();
        if(id !== "_custom"){
            const data = loadLibraryData<TradeGood>("ltg", id ?? "NONE");
            if(data !== undefined){

                newEquipment = {
                    ...data,
                    id: newEquipment.id
                }
            }
        }
        setEditTradeGood(newEquipment);
        setShowAddModal(false);
    }

    const saveTradeGood = (tradeGood: TradeGood) => {
        const index = character.tradeGoods.findIndex(tg => tg.id === tradeGood.id);
        const newTradeGoods = [...character.tradeGoods];
        console.log(index);
        if(index === -1) {
            newTradeGoods.push(tradeGood);
        } else {
            newTradeGoods[index] = tradeGood;
        }
        setCharacter({
            ...character,
            tradeGoods: newTradeGoods
        })
        setEditTradeGood(undefined);
    }

    const handleEditTradeGood = (key: string) => {
        const tradeGood = character.tradeGoods.find(tg => tg.id === key);
        setEditTradeGood(tradeGood);
    }

    const handleRemoveTradeGood = (key: string) => {
        const index = character.tradeGoods.findIndex(tg => tg.id === key);
        if(index !== undefined){
            const newTradeGoods = [...character.tradeGoods]
            newTradeGoods.splice(index,1);
            setCharacter({
                ...character,
                tradeGoods: newTradeGoods
            })
        }
    }

    const handleAddWeapon = (id: string) => {
        const weapon = GetNewWeapon();
        if(id !== "_custom"){
            const data = loadLibraryData<WeaponBlockBase>("lw", id ?? "NONE");
            if(data !== undefined){
                weapon.name = data?.name ?? "";
                weapon.base = data; 
            }
        }
        setEditWeapon(weapon);
        setShowAddModal(false);
    }

    const saveWeapon = (weapon: Weapon) => {
        const index = character.weapons.findIndex(tg => tg.id === weapon.id);
        const newItems = [...character.weapons];
        console.log(index);
        if(index === -1) {
            newItems.push(weapon);
        } else {
            newItems[index] = weapon;
        }
        setCharacter({
            ...character,
            weapons: newItems
        })
        setEditWeapon(undefined);
    }

    const handleEditWeapon = (key: string) => {
        const weapon = character.weapons.find(tg => tg.id === key);
        setEditWeapon(weapon);
    }

    const handleRemoveWeapon = (key: string) => {
        const index = character.weapons.findIndex(i => i.id === key);
        console.log(index);
        if(index !== undefined){
            const newItems = [...character.weapons]
            newItems.splice(index,1);
            setCharacter({
                ...character,
                weapons: newItems
            })
        }
    }

    const onChangeWeapon = (id: string, key: string, value: any) => {
        const index = character.weapons.findIndex(w => w.id === id);
        saveWeapon({
            ...character.weapons[index],
            [key]: value
        })
    }

    const onChangeWeaponState = (id: string, value: string) => {
        const index = character.weapons.findIndex(w => w.id === id);
        saveWeapon({
            ...character.weapons[index],
            carried: value
        })
    }

    const handleAddOutfit = (id: string) => {
        const outfit = GetNewOutfit();
        if(id !== "_custom"){
            const data = loadLibraryData<OutfitBlock>("lo", id ?? "NONE");
            if(data !== undefined){
                outfit.name = data?.name ?? "";
                outfit.base = data;
            }
        }
        
        setEditOutfit(outfit);
        setShowAddModal(false);
    }

    const saveOutfit = (outfit: Outfit) => {
        const index = character.outfits.findIndex(tg => tg.id === outfit.id);
        const newItems = [...character.outfits];
        console.log(index);
        if(index === -1) {
            if(character.outfits.length === 0) {
                outfit.carried = "worn";
            }
            newItems.push(outfit);
        } else {
            newItems[index] = outfit;
        }
        setCharacter({
            ...character,
            outfits: newItems
        })
        setEditOutfit(undefined);
    }

    const handleEditOutfit = (key: string) => {
        const outfit = character.outfits.find(a => a.id === key);
        setEditOutfit(outfit);
    }

    const handleRemoveOutfit = (key: string) => {
        const index = character.outfits.findIndex(tg => tg.id === key);
        if(index !== undefined){
            const newItems = [...character.outfits]
            newItems.splice(index,1);
            setCharacter({
                ...character,
                outfits: newItems
            })
        }
    }

    const onChangeOutfit = (id: string, key: string, value: any) => {
        const index = character.outfits.findIndex(w => w.id === id);
        saveOutfit({
            ...character.outfits[index],
            [key]: value
        })
    }

    const onChangeOutfitState = (id: string, value: string) => {
        const index = character.outfits.findIndex(w => w.id === id);
        saveOutfit({
            ...character.outfits[index],
            carried: value
        })
    }

    const handleAddUtility = (id: string) => {
        let newEquipment = GetNewUtility();
        if(id !== "_custom"){
            const data = loadLibraryData<Equipment>("lut", id ?? "NONE");
            if(data !== undefined){

                newEquipment = {
                    ...data,
                    id: newEquipment.id
                }
            }
        }
        setEditUtility(newEquipment);
        setShowAddModal(false);
    }

    const saveUtility = (utility: Equipment) => {
        const index = character.utilities.findIndex(tg => tg.id === utility.id);
        const newItems = [...character.utilities];
        console.log(index);
        if(index === -1) {
            newItems.push(utility);
        } else {
            newItems[index] = utility;
        }
        setCharacter({
            ...character,
            utilities: newItems
        })
        setEditUtility(undefined);
    }

    const handleEditUtility = (key: string) => {
        const utility = character.utilities.find(a => a.id === key);
        setEditUtility(utility);
    }

    const handleRemoveUtility = (key: string) => {
        const index = character.utilities.findIndex(tg => tg.id === key);
        if(index !== undefined){
            const newItems = [...character.utilities]
            newItems.splice(index,1);
            setCharacter({
                ...character,
                utilities: newItems
            })
        }
    }

    const onChangeUtility = (id: string, key: string, value: any) => {
        const index = character.utilities.findIndex(w => w.id === id);
        saveUtility({
            ...character.utilities[index],
            [key]: value
        })
    }

    const handleAddEquipment = (id: string) => {
        let utility = GetNewUtility("New Equipment");
        if(id !== "_custom"){
            const data = loadLibraryData<Equipment>("lut", id ?? "NONE");
            if(data !== undefined){

                utility = {
                    ...data,
                    id: utility.id
                }
            }
        }
        setEditEquipment(utility);
        setShowAddModal(false);
    }

    const saveEquipment = (equipment: Equipment) => {
        const index = character.equipment.findIndex(tg => tg.id === equipment.id);
        const newItems = [...character.equipment];
        console.log(index);
        if(index === -1) {
            newItems.push(equipment);
        } else {
            newItems[index] = equipment;
        }
        setCharacter({
            ...character,
            equipment: newItems
        })
        setEditEquipment(undefined);
    }

    const handleEditEquipment = (key: string) => {
        const equipment = character.equipment.find(a => a.id === key);
        setEditEquipment(equipment);
    }

    const handleRemoveEquipment = (key: string) => {
        const index = character.equipment.findIndex(tg => tg.id === key);
        if(index !== undefined){
            const newItems = [...character.equipment]
            newItems.splice(index,1);
            setCharacter({
                ...character,
                equipment: newItems
            })
        }
    }

    const onChangeEquipment = (id: string, key: string, value: any) => {
        const index = character.equipment.findIndex(w => w.id === id);
        saveEquipment({
            ...character.equipment[index],
            [key]: value
        })
    }

    const tableList: EquipmentRow[] = [];

    for(const tradeGood of character.tradeGoods) {
        tableList.push({
            id: tradeGood.id,
            icon: <TradeGoodIcon />,
            name: tradeGood.name,
            amount: tradeGood.amount,
            slots: tradeGood.amount * tradeGood.weight,
            actions: <>
                <EditIcon style={{ cursor: "pointer"}} onClick={() => handleEditTradeGood(tradeGood.id)} />
                <CancelIcon style={{ cursor: "pointer"}} onClick={() => handleRemoveTradeGood(tradeGood.id)} />
            </>,
            details: <Grid container marginTop={2}>
                <Grid item xs={12}>{tradeGood.description}</Grid>
                <Grid item xs={8} md={4}>Weight:</Grid>
                <Grid item xs={4} md={2}>{tradeGood.weight}</Grid>
                <Grid item xs={8} md={4}>Slot Conversion:</Grid>
                <Grid item xs={4} md={2}>{tradeGood.conversion}</Grid>
            </Grid>
        })
    }

    for(const weapon of characterDisplay.weapons) {
        const mainWeapon = character.weapons.find(w => w.id === weapon.id);
        if(mainWeapon === undefined)
        {
            continue;
        }
        tableList.push({
            id: weapon.id,
            icon: <WeaponIcon />,
            name: <Stack alignItems="center" direction="row">{weapon.name} {weapon.carried === "held" && <EquipmentBagIcon />}{weapon.isStashed && <StashedIcon />}</Stack>,
            actions: <>
                <SettingsIcon style={{ cursor: "pointer"}} onClick={() => setManageWeapon(mainWeapon)} />
                <EditIcon style={{ cursor: "pointer"}} onClick={() => handleEditWeapon(weapon.id)} />
                <CancelIcon style={{ cursor: "pointer"}} onClick={() => handleRemoveWeapon(weapon.id)} />
            </>,
            influence: weapon.influence,
            knowledge: weapon.knowledge,
            resources: weapon.resources,
            slots: weapon.slots,
            //details: <WeaponDetailDisplay weapon={mainWeapon!} weaponDisplay={weapon} />
        })
    }

    for(const outfit of characterDisplay.outfits) {
        const mainOutfit = character.outfits.find(w => w.id === outfit.id);
        if(mainOutfit === undefined)
        {
            continue;
        }
        tableList.push({
            id: outfit.id,
            icon: <OutfitIcon />,
            name: <>{outfit.name} {outfit.isWorn && <EquipmentBagIcon />}{outfit.isStashed && <StashedIcon />}</>,
            actions: <>
                <SettingsIcon style={{ cursor: "pointer"}} onClick={() => setManageOutfit(mainOutfit)} />
                <EditIcon style={{ cursor: "pointer"}} onClick={() => handleEditOutfit(outfit.id)} />
                <CancelIcon style={{ cursor: "pointer"}} onClick={() => handleRemoveOutfit(outfit.id)} />
            </>,
            influence: outfit.influence,
            knowledge: outfit.knowledge,
            resources: outfit.resources,
            slots: outfit.slots,
            //details: <OutfitDetailDisplay outfit={mainOutfit!} outfitDisplay={outfit} />
        })
    }

    for(const utility of characterDisplay.utility) {
        tableList.push({
            id: utility.id,
            icon: <UtilityIcon />,
            name: utility.name,
            slots: utility.slots,
            influence: utility.influence,
            knowledge: utility.knowledge,
            resources: utility.resources,
            actions: <>
                <EditIcon style={{ cursor: "pointer"}} onClick={() => handleEditUtility(utility.id)} />
                <CancelIcon style={{ cursor: "pointer"}} onClick={() => handleRemoveUtility(utility.id)} />
            </>,
            details: <Grid container marginTop={2}>
            <Grid item xs={12}>{utility.description}</Grid>
            <Grid item xs={8}>Features:</Grid>
            <Grid item xs={4}>{utility.features.length === 0 ? "None" : utility.features.join(", ")}</Grid>
        </Grid>
        })
    }

    for(const equipment of characterDisplay.equipment) {
        tableList.push({
            id: equipment.id,
            icon: <MiscIcon />,
            name: equipment.name,
            slots: equipment.slots,
            influence: equipment.influence,
            knowledge: equipment.knowledge,
            resources: equipment.resources,
            actions: <>
                <EditIcon style={{ cursor: "pointer"}} onClick={() => handleEditEquipment(equipment.id)} />
                <CancelIcon style={{ cursor: "pointer"}} onClick={() => handleRemoveEquipment(equipment.id)} />
            </>,
            details: <Grid container marginTop={2}>
            <Grid item xs={12}>{equipment.description}</Grid>
            <Grid item xs={8}>Features:</Grid>
            <Grid item xs={4}>{equipment.features.length === 0 ? "None" : equipment.features.join(", ")}</Grid>
        </Grid>
        })
    }

    const ManageWeaponDialogue = () => {
        let action = <></>;
        if(manageWeapon !== undefined){
            action = <Grid container spacing={1}>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageWeapon(undefined);
                        onChangeWeaponState(manageWeapon.id, "held");
                    }}>Draw</ButtonFlat></Grid>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageWeapon(undefined);
                        onChangeWeaponState(manageWeapon.id, "carried");
                    }}>Carried</ButtonFlat></Grid>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageWeapon(undefined);
                        onChangeWeaponState(manageWeapon.id, "stored");
                    }}>Stored (Spacecraft)</ButtonFlat></Grid>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageWeapon(undefined);
                    }}>Cancel</ButtonFlat></Grid>
                </Grid>
        }
        return <Dialog open={manageWeapon !== undefined} onClose={() => setManageWeapon(undefined)}>
            <DialogTitle>Weapon state</DialogTitle>
            <DialogContent>
                {action}
            </DialogContent>
        </Dialog>
    }

    const ManageOutfitDialogue = () => {
        let action = <></>;
        if(manageOutfit !== undefined){
            action = <Grid container spacing={1}>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageOutfit(undefined);
                        onChangeOutfitState(manageOutfit.id, "worn");
                    }}>Wear</ButtonFlat></Grid>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageOutfit(undefined);
                        onChangeOutfitState(manageOutfit.id, "carried");
                    }}>Carried</ButtonFlat></Grid>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageOutfit(undefined);
                        onChangeOutfitState(manageOutfit.id, "stored");
                    }}>Stored (Spacecraft)</ButtonFlat></Grid>
                    <Grid item xs={6}><ButtonFlat fullWidth onClick={() => {
                        setManageOutfit(undefined);
                    }}>Cancel</ButtonFlat></Grid>
                </Grid>
        }
        return <Dialog open={manageOutfit !== undefined} onClose={() => setManageOutfit(undefined)}>
            <DialogTitle>Manage outfit state</DialogTitle>
            <DialogContent>
                {action}
            </DialogContent>
        </Dialog>
    }
    const GridRow = (props: { gridSize: GridSize, justifyContent?: "center" | "flex-start" | "flex-end", children?: ReactNode }) => {
        return <Grid item xs={props.gridSize} ><Stack direction="row" justifyContent={props.justifyContent} alignItems="center" height="100%">{props.children}</Stack></Grid>
    }

    const NewEquipmentModal = (props: {show: boolean, onClose: () => void}) => {
        const [ equipmentType, setEquipmentType ] = useState<"None" | "Weapon" | "Outfit" | "Utility" | "Tradegood" | "Equipment">("None");
        const [ searchTerm, setSearchTerm ] = useState("");

        const baseList: InteractableSave[] = [
            {id: 'Weapon', name: "Weapon", onClick: () => setEquipmentType("Weapon")},
            {id: 'Outfit', name: "Outfit", onClick: () => setEquipmentType("Outfit")},
            {id: 'Utility', name: "Utility", onClick: () => setEquipmentType("Utility")},
            {id: 'Tradegood', name: "Trade Good", onClick: () => setEquipmentType("Tradegood")},
            {id: 'Equipment', name: "Equipment", onClick: () => setEquipmentType("Equipment")}
        ];

        const [ itemList, setItemList ] = useState<InteractableSave[]>(baseList);

        const LoadItem = (type: string, id: string) => {
            switch (type) {
                case "lw":
                    handleAddWeapon(id);
                    break;
                case "lo":
                    handleAddOutfit(id);
                    break;
                case "lmi":
                    handleAddEquipment(id);
                    break;
                case "ltg":
                    handleAddTradeGood(id);
                    break;
                case "lut":
                    handleAddUtility(id);
                    break;
            }
        }

        const MakeItemList = (type: string): InteractableSave[] => {
            return makeListData(type, (id) => LoadItem(type, id))
        }

        useEffect(() => {
            switch (equipmentType) {
                case "None":
                    setItemList(baseList);
                    break;
                case "Weapon":
                    setItemList(MakeItemList("lw"));
                    break;
                case "Outfit":
                    setItemList(MakeItemList("lo"));
                    break;
                case "Utility":
                    setItemList(MakeItemList("lut"));
                    break;
                case "Tradegood":
                    setItemList(MakeItemList("tg"));
                    break;
                case "Equipment":
                    setItemList(MakeItemList("lmi"));
                    break;
            }
            setSearchTerm("");
        }, [equipmentType]);

        useEffect(() => { setSearchTerm("")}, [props.show]);

        const actions = [
                {
                    label: "Cancel",    
                    onClick: props.onClose,
                    size:12
                }
            ];

        return <Modal title="Add Equiment" actions={actions} onClose={() => setShowAddModal(true)} open={props.show}>
            {equipmentType !== "None" && <TextField fullWidth
                placeholder="Search"
                type="text" 
                variant="standard"
                value={searchTerm}
                style={{marginBottom: 1}}
                onChange={(event) => setSearchTerm(event.currentTarget.value)} />}
            <HexList
                items={itemList.filter((item) => searchTerm === "" || item.name.toLowerCase().includes(searchTerm.toLowerCase()))}
             />
        </Modal>
    }

    return <Section
        title={`Items ${characterDisplay.equipmentSlotsCurrent}/${characterDisplay.equipmentSlots}`}
        actions={ [
            { action: () => setShowAddModal(true), label: <AddIcon /> }
        ]}>
        <NewEquipmentModal show={showAddModal} onClose={() => setShowAddModal(false)} />
        <ManageWeaponDialogue />
        <ManageOutfitDialogue />
        <TradeGoodEditor setTradeGood={saveTradeGood} tradeGood={editTradeGood} onClose={() => setEditTradeGood(undefined)} />
        <WeaponEditor setWeapon={saveWeapon} weapon={editWeapon} onClose={() => setEditWeapon(undefined)} character={character}  />
        <OutfitEditor setOutfit={saveOutfit} outfit={editOutfit} onClose={() => setEditOutfit(undefined)} character={character}  />
        <UtilityEditor setUtility={saveUtility} utility={editUtility} onClose={() => setEditUtility(undefined)} character={character}  />
        <UtilityEditor setUtility={saveEquipment} utility={editEquipment} onClose={() => setEditEquipment(undefined)} character={character}  />

        <HeadingBar>
            <Grid container paddingX={1}>
                <Grid item xs={1}></Grid>
                <Grid item xs={8}><HeadingBarItem>Name</HeadingBarItem></Grid>
                <Grid item xs={1}><HeadingBarItem tooltip="Slots">S</HeadingBarItem></Grid>
                <Grid item xs={2}><HeadingBarItem></HeadingBarItem></Grid>
            </Grid>
        </HeadingBar>
        <SimpleTable entries={tableList.map(r => {
            return  <Grid container>
                    <GridRow gridSize={1}>{r.icon}</GridRow>
                    <GridRow gridSize={8}><Typography >{r.name}</Typography> {(r.amount ?? 0) > 1 && <sup>({r.amount})</sup>}</GridRow>
                    <GridRow gridSize={1}>{r.slots}</GridRow>
                    <GridRow gridSize={2} justifyContent="flex-end">{r.actions}</GridRow>
                </Grid>
        })} />
    </Section>
}

export default EquipmentList;